import {
	type ExtendedJsonDecimal,
	type Money,
	type WireMoney,
	moneyFromMajorFloat,
	moneyFromWire,
	moneyToWire,
	percentOf,
	subtractMoney,
	sumMoney,
	toMinorUnits,
	zeroMoney,
} from "@performyard/money";

/**
 * Exact aggregation over compensation rows.
 *
 * These wrap the shared money helpers for the one thing this page does over and
 * over: total a column across rows, then compare the total against a budget.
 * Both steps are where float error actually accumulates — a per-row cent of drift
 * is invisible, the same drift across 800 rows is not, and a comparison made on
 * the drifted total can flip a manager's over-budget flag.
 *
 * Money arrives from the API as Extended JSON (`{ "$numberDecimal": "85000.50" }`),
 * which is exact, so a sum built from those values carries no error at all. Draft
 * values typed into the UI are still plain numbers and are accepted too.
 */

/**
 * Sum a money column exactly. Nullish entries count as zero.
 *
 * Accepts both shapes on purpose: stored amounts arrive as Extended JSON, while
 * a manager's unsaved edit is a number from an input field. `moneyFromWire`
 * parses the former with string math; the latter is already a float by the time
 * anyone could hand it here.
 */
export const sumAmounts = (amounts: (WireMoney | number | null | undefined)[]): Money =>
	sumMoney(
		amounts.map((amount) =>
			typeof amount === "number" ? moneyFromMajorFloat(amount) : (moneyFromWire(amount) ?? moneyFromMajorFloat(0))
		)
	);

/**
 * Zero, in the shape the approve request expects.
 *
 * Proposed amounts are nullable in the response but required in the request, so a
 * row a manager never gave an increase to approves as an explicit zero rather than
 * being dropped from the batch.
 */
export const zeroWireMoney: ExtendedJsonDecimal = moneyToWire(zeroMoney());

/**
 * A manager group's budget: the share allocated and snapshotted at cycle
 * creation, which the group's seeded increases sum to exactly.
 *
 * Cycles created before group budgets were allocated have no stored value;
 * those fall back to deriving it as the cycle percent of the group's salary
 * total — the historical behavior, off from the seeds by remainder cents.
 */
export const resolveGroupBudget = (
	stored: WireMoney | null | undefined,
	groupSalary: Money,
	cyclePercent: number | null | undefined
): Money => moneyFromWire(stored) ?? (cyclePercent != null ? percentOf(groupSalary, cyclePercent) : zeroMoney());

/**
 * True when `allocated` exceeds `budget` by any amount — a single cent over is
 * over.
 *
 * The comparison is on exact integer minor units, so it cannot be flipped by
 * representation error the way `toFixed(a, 2) > toFixed(b, 2)` can. An earlier
 * version tolerated one cent on the theory that a lone cent was an artifact of
 * deriving the budget from a percent; budgets are now allocated exactly, so a
 * manager one cent over really did allocate one cent more than the budget on
 * their screen.
 */
export const isOverBudgetMoney = (allocated: Money, budget: Money): boolean =>
	toMinorUnits(allocated) > toMinorUnits(budget);

/**
 * Remaining budget, exactly. Both sides are exact money, so a fully-allocated
 * budget yields exactly zero with no snapping — the ±1-cent snap this used to
 * apply could only hide a real cent of remainder.
 */
export const remainingBudget = (budget: Money, allocated: Money): Money => subtractMoney(budget, allocated);

/**
 * Percent of `budget` consumed by `allocated`, to two decimal places.
 *
 * The ratio is taken over exact minor units on both sides, so only the final
 * quantization to two decimals is inexact — as opposed to dividing two values
 * that have each already drifted.
 */
export const percentAllocated = (allocated: Money, budget: Money): number => {
	const budgetMinorUnits = toMinorUnits(budget);
	if (budgetMinorUnits <= 0) return 0;
	return Math.round((toMinorUnits(allocated) / budgetMinorUnits) * 10000) / 100;
};
